import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { capitalize } from 'utils';
import { Formik, Field } from 'formik';
import { INPUT_REGEX } from 'constants';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { BaseModal } from 'components/UI';
import { SkusActions } from 'state/modules/skus';
import { TaxTypesSelect } from 'components/DataFields';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, MaterialSelectField } from 'components/FormFields';
import { TaxRulesetsSelect, PricingTypesSelect } from 'components/AsyncFields';
import { generateTaxTypes } from 'constants';
import { ability } from '../../../Can';

const Label = styled('span')(({ theme }) => ({
  borderRadius: 11,
  color: theme.palette.text.secondary,
  display: 'inline-block',
  marginTop: 10,
  padding: '5px 18px',
  textTransform: 'capitalize',
  fontSize: 13,
}));

const UpdateSkuModal = ({ open, id, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { fetchingUpdateSku, sku } = useSelector((state) => state.skus);
  const pricingConfig = useSelector((state) => state.config.settings?.core?.pricing || {});
  const TRSEnabled = useSelector(
    (state) => state.config.settings?.core?.pricing?.tax_rules_set?.enabled || false
  );
  const allowTaxPerLocation = TRSEnabled && ability.can('read', 'tax_rules_set');
  const taxTypes = generateTaxTypes(allowTaxPerLocation ? [] : ['PERLOCATION']);
  const taxesEnabled = useSelector((state) => state.config.settings?.core?.receivable?.taxes);

  useEffect(() => {
    if (id) {
      dispatch(SkusActions.fetchSkuById(id));
    }
  }, [id]); // eslint-disable-line

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const getInitialValues = () => {
    const taxing_choice = taxTypes.find(
      (tt) =>
        tt?.value === sku?.taxing_choice ||
        tt?.label?.toLowerCase() === sku?.taxing_choice?.toLowerCase()
    );

    const initialValues = {
      tax_type: sku?.pricing_type || '',
      name: sku?.name || '',
      description: sku?.description || '',
      notes: sku?.notes || '',
      price_unit: sku?.units?.slug || '',
      taxing_choice: taxing_choice ? taxing_choice?.value : sku?.taxing_choice || '',
      tax_rate_default: sku?.tax_rate_default || '',
      tax_rules_set_id: sku?.tax_rules_set?.id || '',
    };
    return initialValues;
  };

  const formSchema = Yup.object().shape({
    tax_type: Yup.object().required(i18n.t('common.required_field')),
    name: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string()
      .required(i18n.t('common.required_field'))
      .matches(INPUT_REGEX, i18n.t('common.input_check')),
    notes: Yup.string().max(
      255,
      i18n.t('common.max_long', {
        max_long: 255,
      })
    ),
    price_unit: Yup.string(),
    taxing_choice: Yup.string().required(i18n.t('common.required_field')),
    tax_rate_default: Yup.number().when('taxing_choice', {
      is: 'DEFAULT',
      then: Yup.number()
        .min(0, i18n.t('common.min_0'))
        .max(1, i18n.t('common.max_1'))
        .required(i18n.t('common.required_field')),
    }),
    tax_rules_set_id: Yup.number().when('taxing_choice', {
      is: 'PERLOCATION',
      then: Yup.number().required(i18n.t('common.required_field')),
    }),
  });

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      enterButtonLabel={i18n.t('common.update')}
      loading={fetchingUpdateSku}
      title={i18n.t('skus.update_information')}
    >
      <Formik
        innerRef={formRef}
        initialValues={getInitialValues()}
        onSubmit={(values) => {
          let data = { ...values };

          data.pricing_type = values?.tax_type?.name;
          delete data.tax_type;

          // remove empty fields
          Object.keys(data).forEach((key) => key !== 'notes' && !data[key] && delete data[key]);

          dispatch(SkusActions.fetchUpdateSku(id, data, handleClick));
        }}
        validationSchema={formSchema}
      >
        {({ handleSubmit, values, errors, touched, setFieldValue }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid data-cy="update-sku-modal" container rowSpacing={2} columnSpacing={2}>
              <Grid size={{ xs: 12 }}>
                <Field
                  errors={errors}
                  touched={touched}
                  name="tax_type"
                  multiple={false}
                  margin="none"
                  label={i18n.t('skus.wizard.type')}
                  customParams={{
                    charge_type: sku?.accounting_type?.is_revenue ? 'revenue' : 'discount',
                  }}
                  value={values.tax_type}
                  allowedUnitTypes={true}
                  setFieldValue={setFieldValue}
                  component={PricingTypesSelect}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Field
                  name="name"
                  label={i18n.t('common.name')}
                  margin={'none'}
                  data-cy="sku_name-input"
                  component={TextField}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Field
                  name="description"
                  label={i18n.t('skus.wizard.invoice_description')}
                  margin={'none'}
                  data-cy="sku_description-input"
                  component={TextField}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Field
                  name="notes"
                  label={i18n.t('skus.wizard.invoice_note')}
                  margin={'none'}
                  required={false}
                  data-cy="sku_notes-input"
                  component={TextField}
                />
              </Grid>
              {values?.tax_type?.allowed_unit_types?.length ? (
                <Grid size={{ xs: 6 }}>
                  <Field
                    name="price_unit"
                    component={MaterialSelectField}
                    label={i18n.t('telemetry.units')}
                    options={
                      values?.tax_type?.allowed_unit_types?.map((val) => {
                        return {
                          value: val,
                          label: capitalize(val?.toLowerCase()),
                        };
                      }) || []
                    }
                    data-cy="sku_units-select"
                    placeholder={i18n.t('telemetry.units')}
                  />
                </Grid>
              ) : (
                ''
              )}

              {taxesEnabled !== false && (
                <>
                  <Grid size={{ xs: 6 }}>
                    <Field
                      margin="none"
                      errors={errors}
                      touched={touched}
                      multiple={false}
                      name="taxing_choice"
                      data-cy="taxing_choice-select"
                      component={TaxTypesSelect}
                      setFieldValue={setFieldValue}
                      label={i18n.t('skus.wizard.select_tax_type')}
                      omitFields={!pricingConfig?.tax_rules_set ? ['PERLOCATION', 'EXTERNAL'] : []}
                    />
                    {(values.taxing_choice === 'EXEMPT' ||
                      values.taxing_choice === i18n.t(`skus.tax_types.EXEMPT`)) && (
                      <Label>{i18n.t('skus.tax_is_set_to_zero')}</Label>
                    )}
                    {(values.taxing_choice === 'EXTERNAL' ||
                      values.taxing_choice === i18n.t(`skus.tax_types.EXTERNAL`)) && (
                      <Label>{i18n.t('skus.tax_zip_code')}</Label>
                    )}
                  </Grid>

                  {(values.taxing_choice === 'DEFAULT' ||
                    values.taxing_choice === i18n.t(`skus.tax_types.DEFAULT`)) && (
                    <Grid size={{ xs: 4 }}>
                      <div style={{ marginLeft: 10 }}>
                        <Field
                          margin="none"
                          asyncErrors={errors?.tax_rate_default}
                          format="price"
                          type="number"
                          decimals={3}
                          label={i18n.t(`skus.default_tax_rate`)}
                          name="tax_rate_default"
                          data-cy="tax_rate_default-input"
                          component={TextField}
                        />
                      </div>
                    </Grid>
                  )}
                  {(values.taxing_choice === 'PERLOCATION' ||
                    values.taxing_choice === i18n.t(`skus.tax_types.PERLOCATION`)) && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <div style={{ marginLeft: 10 }}>
                        <Field
                          margin="none"
                          type="number"
                          label="Tax Ruleset"
                          name="tax_rules_set_id"
                          data-cy="tax_rules_set_id-select"
                          component={TaxRulesetsSelect}
                          asyncErrors={errors?.tax_rules_set_id}
                          customParams={{ rules_set_id: sku?.tax_rules_set?.id || '' }}
                        />
                      </div>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </form>
        )}
      </Formik>
    </BaseModal>
  );
};

UpdateSkuModal.propTypes = {
  current: PropTypes.object,
  open: PropTypes.bool.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func.isRequired,
};

UpdateSkuModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default UpdateSkuModal;
